import React, { useCallback, useContext, useEffect, useLayoutEffect, useState } from 'react';
import { ArrowForward, Search } from '@mui/icons-material';
import { Box, Button, Drawer, TextField, Tooltip } from '@mui/material';
import { useSelector } from 'store/hooks';
import { SearchParams } from 'api/ResourceAPI';
import useApi from 'hooks/useApi';
import { useTranslation } from 'react-i18next';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { clearSelectedFilterAction } from 'store/actions/filterActions';
import { closeEditDrawerAction, openEditDrawerAction } from './__context/_reducers/EditDrawer/EditDrawer';
import { injectBaselineAffectation } from './__context/_reducers/InjectBaselineAffectation/InjectBaselineAffectation';
import { injectOrderAction } from './__context/_reducers/InjectOrder/InjectOrder';
import { gridFinishedLoadingAction, gridIsLoadingAction } from './__context/_reducers/GridHandler/GridHandler';
import { BaselineAffectationContext, BaselineAffectationProvider } from './__context/BaselineAffectationContext';
import AffectationGrid from './AffectationGrid/AffectationGrid';
import { useAppState } from 'store/Provider';

import ChangeBaseLineAPIServices from 'api/ChangeBaseLineAPIServices';
import OrderApiService from 'api/OrderApiService';
import { injectScopes } from './__context/_reducers/InjectScopes/InjectScopes';
import { setAffectationConsultantsAction } from './__context/_reducers/SetAffectationConsultants/SetAffectationConsultants';
import { setAffectationClientsAction } from './__context/_reducers/SetAffectationClients/SetAffectationClients';
import { bulkUpdateBaselineAction } from './__context/_reducers/UpdateBaseline/UpdateBaseline';
import UserApiService from 'api/UserApiService';
import User from 'types/entities/User';
import OrderWorkunit from 'types/entities/OrderWorkunit';
import { setAffectationDMAction } from './__context/_reducers/SetAffectationDM/SetAffectationDM';
import OrderWorkunitAffectationEdit from './AffectationGrid/OrderWorkunitAffectationEdit';
import { handleApiError } from 'api/errors';
import { includes } from 'lodash';
import Order from 'types/entities/Order';
import { setSnackbarAction } from 'store/actions/snackbarActions';

const BaselineAffectation = () => {
  const { dispatch } = useAppState();
  const { makeCall } = useApi();
  const [t] = useTranslation();
  const order = useSelector((state) => state?.deliverable?.order);
  const { baseLineOrder, sourceOrder } = useSelector((state) => state.changeBaseline);
  const appState = useSelector((state) => state.app);

  const [isLoading, setIsLoading] = useState(false);
  const [workunitsSaving, setWorkunitsSaving] = useState<number>(0);
  const appFiltersSelected = useSelector((state) => state.filter.selected);
  const [textSearch, setTextSearch] = useState('');
  useLayoutEffect(() => {
    dispatch(clearSelectedFilterAction());
    return () => {
      dispatch(clearSelectedFilterAction());
    };
  }, []);
  // useEffect(() => {
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);
  const { state, dispatch: dispatchBLOrderWorkunitContext } = useContext(BaselineAffectationContext);

  const openEditDrawer = () => dispatchBLOrderWorkunitContext(openEditDrawerAction());
  const closeEditDrawer = () => dispatchBLOrderWorkunitContext(closeEditDrawerAction());

  useEffect(() => {
    const getPoScopeByBaseline = async (order: any) => {
      const { order_scopes } = await OrderApiService.fetchPoScopeBaseline(
        Number(baseLineOrder?.id),
        2,
        order?.affair_otp
      );
      dispatchBLOrderWorkunitContext(injectScopes(order_scopes?.datas));
    };

    const getAffectationConsultants = async () => {
      if (appState.customer && appState.customer.id) {
        const affectableConsultants: User[] = await makeCall(
          UserApiService.fetchPossiblesConsultants(),
          'Unable to retrieve consultants'
        );
        dispatchBLOrderWorkunitContext(setAffectationConsultantsAction(affectableConsultants));
      }
    };
    const getClients = async () => {
      if (appState.customer) {
        const res = await makeCall(
          UserApiService.getClientsOnCustomer(appState.customer?.id, { is_archived: 0, size: -1 }),
          'Error while loading clients'
        );
        dispatchBLOrderWorkunitContext(setAffectationClientsAction(res?.datas || []));
      }
    };

    if (appState.customer?.id) {
      getPoScopeByBaseline(order);
      getAffectationConsultants();
      getClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appState.customer]);

  useEffect(() => {
    const getDeliveryManagers = async () => {
      try {
        const res = await UserApiService.fetchPossiblesDeliverableTeamLeaders();

        const sortedRes = res.sort((a, b) => {
          const nameA = a.last_name.toUpperCase();
          const nameB = b.last_name.toUpperCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });

        dispatchBLOrderWorkunitContext(setAffectationDMAction(sortedRes));
      } catch (error) {
        handleApiError(error);
      }
    };

    if (appState.customer?.id) getDeliveryManagers();
  }, [appState.customer?.id]);

  const fetchOrderWorkunits = useCallback(
    async (searchParams: SearchParams) => {
      const { datas, totalPages, totalItems, currentPage } = await makeCall(
        ChangeBaseLineAPIServices.fetchBaseLineAffectationMissions(Number(baseLineOrder?.id), 3, searchParams),
        'Unable to retrieve order workunits',
        'affectationGrid'
      );
      dispatchBLOrderWorkunitContext(injectBaselineAffectation(datas, totalItems, totalPages, currentPage));
      dispatchBLOrderWorkunitContext(gridFinishedLoadingAction());
      if (baseLineOrder) {
        dispatchBLOrderWorkunitContext(injectOrderAction(baseLineOrder));
      }
    },
    [dispatchBLOrderWorkunitContext]
  );

  const constructSearchParams = useCallback(() => {
    const searchParams: SearchParams = {
      // order_id: parseInt(match.params.order_id, 10),
      // order_id: baseLineOrder?.id,
      size: state.pageSize,
      page: state.currentPage,
      is_archived: false,
      join: ['scope', 'consultant', 'client', 'delivery_manager'],
      // workunit_id: '>=0', // custom value so removed here
    };

    if (Object.values(appFiltersSelected).length) {
      Object.entries(appFiltersSelected).forEach(([field, value]) => {
        if (field === 'text_search') return;
        if (field.includes('_id') && value[0] && value[0] !== '') {
          searchParams[field] = value;
        } else if (value[0] && value[0] !== '') {
          searchParams[field] = `:${value}:`;
        }
      });
    }
    if (textSearch) {
      searchParams['workunit_name,workunit_reference'] = `:${textSearch}:`;
    }
    return searchParams;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseLineOrder?.id, appFiltersSelected, state.currentPage, state.pageSize, textSearch]);

  useEffect(() => {
    let mounted = true;
    dispatchBLOrderWorkunitContext(gridIsLoadingAction());
    if (baseLineOrder?.id && mounted) {
      const delayDebounceFn = setTimeout(() => {
        const searchParams = constructSearchParams();
        fetchOrderWorkunits(searchParams);
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
    return () => {
      mounted = false;
    };
  }, [constructSearchParams, dispatchBLOrderWorkunitContext, fetchOrderWorkunits, baseLineOrder?.id]);

  const [isDrawerLoading, setIsDrawerLoading] = useState<boolean>(false);
  const handleValidateSelectionDrawer = async (multiSelect: Partial<OrderWorkunit>) => {
    setIsDrawerLoading(true);
    await saveManyOrderWorkunitsChanges(multiSelect);
  };
  const saveManyOrderWorkunitsChanges = async (multiSelect: Partial<OrderWorkunit>) => {
    try {
      if (!state?.baseLineOrderWorkunits[0]) throw Error('No registered order');
      const baseLinePara = {
        page_typeid: 3,
        workunit_type: 'multiple',
        order_baseline_id: baseLineOrder?.id,
        source_order_id: order?.id,
        baseline_version:
          sourceOrder?.baseline_version === 0 || sourceOrder?.baseline_version == null
            ? 1
            : sourceOrder?.baseline_version + 1,
      };
      const toSend: {
        ressources: number[];
        data: {
          consultant_id?: User['id'];
          consultant_fullname?: string;
          scope_id?: User['id'];
          forecast_date?: string;
          client_id?: User['id'];
          client_fullname?: string;
          order_baseline_id: number;
          source_order_id: number;
          delivery_manager_id?: User['id'];
          delivery_manager_fullname?: string;
          status: 1;
        };
      } = {
        ressources: state.selectedItems as unknown as number[],
        data: {
          order_baseline_id: Number(baseLineOrder?.id),
          source_order_id: Number(order?.id),
          status: 1,
          // order_id: state.order?.id,
        },
      };

      const copiedData = { ...multiSelect };
      if (multiSelect?.consultant_id) {
        const consultant = state.consultants?.find((c) => c.id === multiSelect.consultant_id);
        copiedData.consultant = consultant;
        copiedData.consultant_fullname = `${consultant?.last_name.toLocaleUpperCase()} ${consultant?.first_name}`;
        toSend.data.consultant_id = multiSelect?.consultant_id;
        toSend.data.consultant_fullname = `${consultant?.last_name.toLocaleUpperCase()} ${consultant?.first_name}`;
      }
      if (multiSelect?.forecast_date) {
        toSend.data.forecast_date = multiSelect?.forecast_date;
      }
      if (multiSelect?.client_id) {
        const client = state.clients?.find((c) => c.id === multiSelect.client_id);
        copiedData.client = client;
        copiedData.client_fullname = `${client?.last_name.toLocaleUpperCase()} ${client?.first_name}`;
        toSend.data.client_id = multiSelect?.client_id;
        toSend.data.client_fullname = `${client?.last_name.toLocaleUpperCase()} ${client?.first_name}`;
      }
      if (multiSelect?.scope_id) {
        toSend.data.scope_id = multiSelect?.scope_id;
      }
      if (multiSelect?.delivery_manager_ids) {
        const deliveryManagers = state.deliveryManagers?.filter((c) =>
          includes(multiSelect.delivery_manager_ids, c.id)
        );
        copiedData.delivery_manager = deliveryManagers;
        copiedData.delivery_manager_fullname = `${deliveryManagers[0]?.last_name.toLocaleUpperCase()} ${
          deliveryManagers[0]?.first_name
        }`;
        toSend.data.delivery_manager_id = deliveryManagers[0]?.id;
        toSend.data.delivery_manager_fullname = `${deliveryManagers[0]?.last_name.toLocaleUpperCase()} ${
          deliveryManagers[0]?.first_name
        }`;
      }

      if (state?.order?.id) {
        await ChangeBaseLineAPIServices.bulkUpdate(baseLinePara, toSend.ressources, toSend.data, false);
        dispatchBLOrderWorkunitContext(bulkUpdateBaselineAction(copiedData));
      }
    } catch (error) {
      const result = (error as Error).message;
      if (result) {
        dispatch(
          setSnackbarAction({
            message: `Error saving multiples order-workunits : ${result}`,
            open: true,
            severity: 'error',
          })
        );
      } else
        dispatch(
          setSnackbarAction({ message: 'Error saving multiples order-workunits', open: true, severity: 'error' })
        );
      handleApiError(error);
    } finally {
      setIsDrawerLoading(false);
      closeEditDrawer();
    }
  };

  return (
    <Box sx={{ mb: 5, mt: 5 }}>
      <div className="bg-gray-100 p-4 w-full">
        {/* Quick Access Tabs */}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            Loading order data...
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex' }}>
              <Button
                variant="contained"
                onClick={openEditDrawer}
                disabled={!state.selectedItems.length}
                sx={{ mb: 4, mr: 2 }}
              >
                Set
              </Button>

              <TextField
                placeholder={t('Search by name or reference')}
                variant="standard"
                InputProps={{
                  endAdornment: <Search />,
                }}
                value={textSearch}
                onChange={(e) => setTextSearch(e.currentTarget.value)}
                className="search_textfield"
                sx={{ mb: 4, ml: 'auto' }}
              />
            </Box>
            <AffectationGrid
              workunitSaving={workunitsSaving}
              setWorkunitsSaving={setWorkunitsSaving}
              orderId={order?.id as any}
              sourceOrder={sourceOrder as any}
            />
            {/* <Box sx={{ display: 'flex' }}>
            <Tooltip title="All rows without a scope will not appear on the next view" arrow>
              <Button
                variant="contained"
                endIcon={<ArrowForward />}
                onClick={() =>
                  history.push(`/${appState.customer?.slug}/orders/workunits/summary/${order_id}`)
                }
                disabled={isLoading || workunitsSaving > 0}
                sx={{ ml: 'auto' }}
              >
                {t('Next')}
              </Button>
            </Tooltip>
          </Box> */}

            <Drawer
              anchor="right"
              open={state.isEditDrawerOpen}
              onClose={closeEditDrawer}
              PaperProps={{
                sx: {
                  width: {
                    xs: '100%',
                    lg: 450,
                  },
                },
              }}
            >
              <OrderWorkunitAffectationEdit
                consultants={state?.consultants}
                customers={state?.clients}
                deliveryManagers={state?.deliveryManagers}
                validateSelectedOptions={handleValidateSelectionDrawer}
                isLoading={isDrawerLoading}
                order={state?.order}
                dispatchOpen={closeEditDrawer}
              />
            </Drawer>
          </>
        )}
      </div>
    </Box>
  );
};

// export default BaselineAffectation;
export default (props: any) =>
  React.useMemo(
    () => (
      <BaselineAffectationProvider>
        <BaselineAffectation {...props} />
      </BaselineAffectationProvider>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
